import React from "react";
import { useTranslation } from "react-i18next";
import { HailingStepData, INITIAL_HAILING_STATE, selectedVehicleClasses } from "./mock/data";
import MapView from "./components/MapView";
import WhereTo from "./steps/WhereTo";
import HailingHeader from "./components/HailingHeader";
import SignInDialog from "./components/SignInDialog";
import { useSearchParams } from "react-router-dom";
import Prices, { fareFormatter, highestFare, toPricedVehicles } from "./steps/Prices";
import Payment from "./steps/Payment";
import RideTracker from "./steps/RideTracker";
import TripHistory from "./trips/TripHistory";
import SupportModal from "./components/SupportModal";
import { getCurrentIdToken } from "../../firebase/firebaseClient";
import { isScheduleTooSoon } from "./schedule";
import { DRIVER_ROUTE_STATUSES } from "./driverRoute";
import {
  TERMINAL_ORDER_STATUSES,
  quoteIsFresh,
  restoreActiveOrder,
} from "./hailingFlow.helpers";
import {
  HailingQuoteResponse,
  MeHailingQuoteService,
} from "../../api/meHailingQuote.service";
import { MeHailingOrderService } from "../../api/meHailingOrder.service";

type QuoteStatus = "idle" | "loading" | "success" | "error";

interface QuoteState {
  routeKey: string;
  status: QuoteStatus;
  data?: HailingQuoteResponse;
}

// Main HailingFlow component - manages overall state
const HailingFlow: React.FC = () => {
  const [flowState, setFlowState] = React.useState<HailingStepData>({
    ...INITIAL_HAILING_STATE,
  });
  // The rider's booked (or restored) rides, kept apart from the booking form so that
  // starting a new booking on Ride never redraws or overwrites a trip on My trips.
  // A rider can hold several at once (e.g. a live ride and a scheduled one).
  const [activeTrips, setActiveTrips] = React.useState<HailingStepData[]>([]);
  // The ride whose tracker is open; the map, cancel and status below are about it.
  const [selectedTripId, setSelectedTripId] = React.useState<string | null>(null);
  const activeTrip =
    activeTrips.find((trip) => trip.hailingRequestId === selectedTripId) ?? null;
  // Each ride's latest known status, keyed by hailing request id.
  const [tripStatuses, setTripStatuses] = React.useState<Record<string, string>>({});
  const [driverPosition, setDriverPosition] = React.useState<
    { lat: number; lng: number } | undefined
  >();

  const { t, i18n } = useTranslation("hailing");
  const currentLanguageRef = React.useRef(i18n.language);
  currentLanguageRef.current = i18n.language;
  const quotePlaceIds = React.useMemo(
    () =>
      [
        flowState.pickupPlaceId,
        flowState.stopPlaceId,
        flowState.dropoffPlaceId,
      ].filter(Boolean),
    [flowState.pickupPlaceId, flowState.stopPlaceId, flowState.dropoffPlaceId],
  );
  const scheduledTime = flowState.scheduledTime;
  const scheduleTooSoon = isScheduleTooSoon(scheduledTime);
  // The pickup time is part of the key: a scheduled ride is priced for its time, so
  // changing it needs a new quote.
  const quoteRouteKey =
    flowState.pickupPlaceId && flowState.dropoffPlaceId
      ? JSON.stringify([...quotePlaceIds, scheduledTime ?? "now"])
      : null;
  const currentQuoteRouteRef = React.useRef<string | null>(quoteRouteKey);
  currentQuoteRouteRef.current = quoteRouteKey;
  const quoteTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const quoteRequestRef = React.useRef<{
    routeKey: string;
    promise: Promise<HailingQuoteResponse | null>;
  } | null>(null);
  const quoteRequestIdRef = React.useRef(0);
  const [quoteState, setQuoteState] = React.useState<QuoteState | null>(null);
  const [submittingPrices, setSubmittingPrices] = React.useState(false);
  const [submittingOrder, setSubmittingOrder] = React.useState(false);
  const [cancellingOrder, setCancellingOrder] = React.useState(false);
  // A cancelled ride stays on screen (with any fee charged) until the rider
  // books a new one, as the web receipt does.
  const [orderCancelled, setOrderCancelled] = React.useState(false);
  const [cancelOrderError, setCancelOrderError] = React.useState(false);
  const [orderError, setOrderError] = React.useState(false);
  // Set when re-quoting for the chosen card changed the fare; the rider confirms again.
  const [fareChanged, setFareChanged] = React.useState(false);
  const orderSubmissionRef = React.useRef(false);

  // Signed-in flag: set when the sign-in step completes. Kept as explicit
  // state (not derived from currentStep) so the "See Prices" button can
  // check it while still on the "where" screen.
  const [authed, setAuthed] = React.useState(false);
  const [authResolved, setAuthResolved] = React.useState(false);
  const signedIn = authed;

  // Looks up the rider's active orders and, when there are any, keeps them all and opens
  // the latest one's live tracker. Runs on page load for a restored session and again
  // after signing in, so a rider who signs in with a ride under way lands on it without
  // a refresh. Resolves true when a trip was restored.
  const restoreActiveTrip = async (isCancelled: () => boolean) => {
    try {
      const activeOrders = await new MeHailingOrderService().getActiveOrders();
      if (isCancelled()) return false;
      const restored = activeOrders
        .filter((order) => order.hailingRequestId)
        .flatMap((order) => {
          const state = restoreActiveOrder(order, currentLanguageRef.current);
          if (!state) {
            console.error(
              "Could not restore an active hailing order because its route or fare details are incomplete.",
            );
            return [];
          }
          return [{ state, status: order.status.toUpperCase() }];
        });
      const latest = restored[restored.length - 1];
      if (!latest) return false;
      setActiveTrips(restored.map(({ state }) => state));
      setTripStatuses(
        Object.fromEntries(
          restored.map(({ state, status }) => [state.hailingRequestId!, status]),
        ),
      );
      setSelectedTripId(latest.state.hailingRequestId ?? null);
      setOrderCancelled(false);
      setFlowState((prev) => ({ ...prev, currentStep: "ride" }));
      setDriverPosition(latest.state.driverPosition);
      return true;
    } catch (error) {
      if (!isCancelled()) {
        console.error("Failed to restore active hailing orders.", error);
      }
      return false;
    }
  };

  // Restore the signed-in session before looking up active orders, so refresh
  // reopens the rider's current trip instead of always returning to lookup.
  React.useEffect(() => {
    let cancelled = false;
    const restoreSession = async () => {
      try {
        const token = await getCurrentIdToken();
        if (cancelled) return;
        if (token) {
          setAuthed(true);
          await restoreActiveTrip(() => cancelled);
        }
        if (!cancelled) setAuthResolved(true);
      } catch {
        // No session (or Firebase unreachable) — the flow simply stays
        // signed out; the sign-in gates above handle that state.
        if (!cancelled) setAuthResolved(true);
      }
    };
    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // Controls the additional "See Prices" gate. Signed-out users are already
  // required to sign in before they can search for places.
  const REQUIRE_SIGN_IN = true;

  const isZh = i18n.language.startsWith("zh");
  const quoteLanguage = isZh ? "zh-HK" : "en";

  const requestQuote = React.useCallback(
    (
      routeKey: string,
      placeIds: string[],
      language: string,
      time: string | null,
      forceRefresh = false,
    ): Promise<HailingQuoteResponse | null> => {
      const pending = quoteRequestRef.current;
      if (pending?.routeKey === routeKey) {
        if (!forceRefresh) return pending.promise;
        return pending.promise.then(() =>
          requestQuote(routeKey, placeIds, language, time, true),
        );
      }

      const requestId = ++quoteRequestIdRef.current;
      setQuoteState({ routeKey, status: "loading" });
      let request: Promise<HailingQuoteResponse | null>;
      request = new MeHailingQuoteService()
        .getQuote({ placeIds, language, ...(time ? { time } : {}) })
        .then((data) => {
          if (
            currentQuoteRouteRef.current === routeKey &&
            quoteRequestIdRef.current === requestId
          ) {
            setQuoteState({ routeKey, status: "success", data });
          }
          return data;
        })
        .catch(() => {
          if (
            currentQuoteRouteRef.current === routeKey &&
            quoteRequestIdRef.current === requestId
          ) {
            setQuoteState({ routeKey, status: "error" });
          }
          return null;
        })
        .finally(() => {
          if (quoteRequestRef.current?.promise === request) {
            quoteRequestRef.current = null;
          }
        });
      quoteRequestRef.current = { routeKey, promise: request };
      return request;
    },
    [],
  );

  const currentQuote =
    quoteState?.routeKey === quoteRouteKey && quoteState.status === "success"
      ? quoteState.data
      : undefined;

  React.useEffect(() => {
    if (
      !signedIn ||
      !quoteRouteKey ||
      scheduleTooSoon ||
      flowState.currentStep !== "where" ||
      (quoteState?.routeKey === quoteRouteKey &&
        (quoteState.status === "loading" ||
          quoteState.status === "error" ||
          (quoteState.status === "success" && quoteIsFresh(quoteState.data))))
    ) {
      return;
    }

    quoteTimerRef.current = setTimeout(() => {
      quoteTimerRef.current = null;
      void requestQuote(quoteRouteKey, quotePlaceIds, quoteLanguage, scheduledTime);
    }, 2000);
    return () => {
      if (quoteTimerRef.current !== null) {
        clearTimeout(quoteTimerRef.current);
        quoteTimerRef.current = null;
      }
    };
  }, [
    flowState.currentStep,
    quoteLanguage,
    quotePlaceIds,
    quoteRouteKey,
    quoteState,
    requestQuote,
    scheduleTooSoon,
    scheduledTime,
    signedIn,
  ]);

  const goToStep = (step: string) => {
    setFlowState((prev) => ({ ...prev, currentStep: step }));
  };

  // The open ride's latest status, so a tracker opened again starts from it rather than
  // from "finding a driver" while its first refresh is on the way.
  const activeTripStatus = selectedTripId ? tripStatuses[selectedTripId] : undefined;

  const handleTripStatus = React.useCallback(
    (status: string) => {
      if (!selectedTripId) return;
      setTripStatuses((prev) =>
        prev[selectedTripId] === status ? prev : { ...prev, [selectedTripId]: status },
      );
    },
    [selectedTripId],
  );

  // Rides still under way, for My trips. A rider may book more than one at a time
  // (a live ride and scheduled ones), so booking never waits for these to end.
  const openTrips = activeTrips.filter((trip) => {
    const id = trip.hailingRequestId!;
    if (orderCancelled && id === selectedTripId) return false;
    return !TERMINAL_ORDER_STATUSES.includes(tripStatuses[id] ?? "");
  });

  const handleCreateOrder = async (
    paymentInstrumentId: string,
    paymentMethod: HailingStepData["paymentMethod"],
  ) => {
    if (orderSubmissionRef.current) return;

    const { vehicleClass } = flowState;
    if (!quoteRouteKey || !vehicleClass.vehicleClassCode) {
      setOrderError(true);
      return;
    }
    // The pickup time got too close while the rider was choosing: back to the
    // Where step, which says so and lets them pick a later time.
    if (isScheduleTooSoon(flowState.scheduledTime)) {
      goToStep("where");
      return;
    }

    orderSubmissionRef.current = true;
    setSubmittingOrder(true);
    setOrderError(false);
    setFareChanged(false);
    try {
      // The backend charges the card the quote was priced for, so the quote from the
      // Prices step fails (HL_042) when the rider has since added or switched card.
      // Re-quote the same route for the chosen card instead of sending them back.
      const quote = await new MeHailingQuoteService().getQuote({
        placeIds: quotePlaceIds,
        language: quoteLanguage,
        paymentInstrumentId,
        ...(flowState.scheduledTime ? { time: flowState.scheduledTime } : {}),
      });
      quoteRequestIdRef.current += 1;
      setQuoteState({ routeKey: quoteRouteKey, status: "success", data: quote });

      const money = fareFormatter(i18n.language);
      const quotedVehicles = toPricedVehicles(quote.quotes ?? [], quote.quoteId, money);
      // Every class the rider picked must still be quoted for this card.
      const repricedClasses = selectedVehicleClasses(flowState).map((selected) =>
        quotedVehicles.find((vehicle) => vehicle.quoteOptionKey === selected.quoteOptionKey),
      );
      const repricedList = repricedClasses.filter(
        (vehicle): vehicle is NonNullable<typeof vehicle> => vehicle !== undefined,
      );
      const repriced = highestFare(repricedList);
      if (!repriced || repricedList.length !== repricedClasses.length) {
        setOrderError(true);
        return;
      }
      // A card can carry its own discount: show the new fare and let the rider confirm it.
      if (repriced.amount !== vehicleClass.flat) {
        setFlowState((prev) => ({
          ...prev,
          vehicleClass: repriced,
          vehicleClasses: repricedList,
          fareEstimate:
            repricedList.length > 1
              ? t("steps.prices.upTo", { amount: money.format(repriced.amount) })
              : money.format(repriced.amount),
        }));
        setFareChanged(true);
        return;
      }

      const operatingAreas = [
        ...new Set(repricedList.flatMap((vehicle) => vehicle.operatingArea ?? [])),
      ];
      const result = await new MeHailingOrderService().createOrder({
        quoteId: quote.quoteId,
        preferVehicleClasses: [
          ...new Set(
            repricedList.map((vehicle) => vehicle.vehicleClassCode ?? vehicleClass.vehicleClassCode!),
          ),
        ],
        paymentInstrumentId,
        ...(operatingAreas.length ? { operatingAreas } : {}),
      });
      // The quote id becomes the hail's own id, so it stands in if the response omits it.
      const hailingRequestId = result?.hailingRequestId ?? quote.quoteId;
      setActiveTrips((prev) => [
        // A cancelled ride on screen gives way to the new one.
        ...prev.filter(
          (trip) => !(orderCancelled && trip.hailingRequestId === selectedTripId),
        ),
        {
          ...flowState,
          currentStep: "ride",
          vehicleClass: repriced,
          vehicleClasses: repricedList,
          paymentMethod,
          ...(quote.encodedPolyline
            ? { routePolyline: quote.encodedPolyline }
            : {}),
          hailingRequestId,
        },
      ]);
      setSelectedTripId(hailingRequestId);
      setOrderCancelled(false);
      setCancelOrderError(false);
      setDriverPosition(undefined);
      // The booking form starts over; the new ride's tracker opens on Ride.
      setFlowState({ ...INITIAL_HAILING_STATE, currentStep: "ride" });
    } catch (error) {
      console.error("Failed to create hailing order.", error);
      setOrderError(true);
    } finally {
      orderSubmissionRef.current = false;
      setSubmittingOrder(false);
    }
  };

  const handleCancelOrder = async () => {
    const hailingRequestId = activeTrip?.hailingRequestId;
    if (!hailingRequestId || orderSubmissionRef.current) {
      setCancelOrderError(true);
      return;
    }
    setCancellingOrder(true);
    setCancelOrderError(false);
    try {
      await new MeHailingOrderService().cancelOrder(hailingRequestId);
      setOrderCancelled(true);
      setDriverPosition(undefined);
    } catch (error) {
      console.error("Failed to cancel hailing order.", error);
      setCancelOrderError(true);
    } finally {
      setCancellingOrder(false);
    }
  };

  // A fresh booking. The rider's other rides stay; a cancelled one on screen is dropped.
  const restart = () => {
    setFlowState({ ...INITIAL_HAILING_STATE });
    if (orderCancelled) {
      setActiveTrips((prev) =>
        prev.filter((trip) => trip.hailingRequestId !== selectedTripId),
      );
    }
    setSelectedTripId(null);
    setDriverPosition(undefined);
    setOrderError(false);
    setFareChanged(false);
    setOrderCancelled(false);
    setCancelOrderError(false);
  };

  // Shared sign-in view — driven by the ?login=1 query param while HailingFlow
  // stays mounted, preserving booking progress and the signed-in flag.
  const [searchParams, setSearchParams] = useSearchParams();
  const loginOpen = searchParams.get("login") === "1";
  // Why the view opened: "book" advances to prices after signing in from the
  // booking flow; "account" just authenticates (header / My trips).
  const [loginIntent, setLoginIntent] = React.useState<"account" | "book">(
    "account",
  );

  // Adds (?login=1) or removes the sign-in view's query param. User-initiated
  // opens push a history entry; the required initial gate replaces the URL.
  const setLoginParam = (open: boolean, replace: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (open) next.set("login", "1");
    else next.delete("login");
    setSearchParams(next, { replace });
  };

  // Signed-out visitors must authenticate before using the booking flow.
  // Replace the current URL so browser Back leaves the page rather than
  // dismissing the required sign-in view.
  React.useEffect(() => {
    if (!authResolved || signedIn || loginOpen) return;
    const next = new URLSearchParams(searchParams);
    next.set("login", "1");
    setSearchParams(next, { replace: true });
  }, [authResolved, signedIn, loginOpen, searchParams, setSearchParams]);

  // After successful sign-in, return to the lookup screen while preserving
  // booking progress.
  const closeLogin = () => {
    // Going back to the booking flow: keep the "login" param off so the
    // lookup screen (WhereTo) stays active and the signed-in flag still
    // reflects where we left off.
    setLoginParam(false, true);
    // Keep the user's place in the booking flow. If they were coming from
    // the "where" step, return them there so they can pick up where they
    // left off (this matches the lookup page they came from).
    setFlowState((prev) => ({ ...prev, currentStep: "where" }));
  };

  const openLogin = (intent: "account" | "book") => {
    setLoginIntent(intent);
    setLoginParam(true, false); // push — Back / Escape closes the view
  };

  const refreshQuoteAndShowPrices = async () => {
    if (!quoteRouteKey || scheduleTooSoon) return;

    if (quoteTimerRef.current !== null) {
      clearTimeout(quoteTimerRef.current);
      quoteTimerRef.current = null;
    }
    setSubmittingPrices(true);
    try {
      const quote = await requestQuote(
        quoteRouteKey,
        quotePlaceIds,
        quoteLanguage,
        scheduledTime,
        true,
      );
      if (quote && currentQuoteRouteRef.current === quoteRouteKey) {
        goToStep("prices");
      }
    } finally {
      setSubmittingPrices(false);
    }
  };

  const handleSeePrices = async () => {
    if (REQUIRE_SIGN_IN && !signedIn) {
      openLogin("book");
      return;
    }
    await refreshQuoteAndShowPrices();
  };

  const handleLoginSuccess = async () => {
    setAuthed(true);
    closeLogin();
    // A ride already under way (e.g. signed in again after clearing the browser)
    // opens straight on its tracker, on the Ride tab, as a refresh would.
    const restored = await restoreActiveTrip(() => false);
    if (restored) {
      setActiveTab("ride");
      return;
    }
    // Booking flow continues to prices; account-only sign-in stays where it was.
    if (loginIntent === "book") void refreshQuoteAndShowPrices();
  };

  // Lock background scroll while the sign-in modal is open (the dialog itself
  // scrolls via max-h + overflow-y-auto).
  React.useEffect(() => {
    if (!loginOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [loginOpen]);

  // The sign-in view has no X, backdrop-click, or Escape dismissal while the
  // user is signed out.

  // Header tabs: "ride" = booking flow, "trips" = My trips (sign-in gated)
  const [activeTab, setActiveTab] = React.useState<"ride" | "trips">("ride");
  // My trips opens on the list of trip cards; the live ride's card opens its tracker.
  const [tripsView, setTripsView] = React.useState<"list" | "tracker">("list");
  // Mobile nav dropdown — opened by tapping the logo tile (below md only)
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [supportOpen, setSupportOpen] = React.useState(false);

  const activeTripsRef = React.useRef(activeTrips);
  activeTripsRef.current = activeTrips;

  // Brings My trips up to date: a ride's tracker only refreshes while it is open, so a
  // ride that ended (or one booked elsewhere) would otherwise keep its old card. The
  // active orders give the rides still under way; a ride we hold that is no longer among
  // them is looked up on its own, so its final status moves it to the past trips.
  const refreshOpenTrips = React.useCallback(async () => {
    try {
      const service = new MeHailingOrderService();
      const orders = await service.getActiveOrders();
      const current = orders
        .filter((order) => order.hailingRequestId)
        .flatMap((order) => {
          const state = restoreActiveOrder(order, currentLanguageRef.current);
          return state ? [{ state, status: order.status.toUpperCase() }] : [];
        });
      const currentIds = new Set(current.map(({ state }) => state.hailingRequestId));
      const gone = await Promise.all(
        activeTripsRef.current
          .filter((trip) => !currentIds.has(trip.hailingRequestId))
          .map(async (trip) => {
            try {
              const order = await service.getOrder(trip.hailingRequestId!);
              return [[trip.hailingRequestId!, order.status.toUpperCase()] as const];
            } catch {
              return [];
            }
          }),
      );
      setActiveTrips((prev) => [
        ...prev,
        ...current
          .filter(({ state }) => !prev.some((trip) => trip.hailingRequestId === state.hailingRequestId))
          .map(({ state }) => state),
      ]);
      setTripStatuses((prev) => ({
        ...prev,
        ...Object.fromEntries(
          current.map(({ state, status }) => [state.hailingRequestId!, status]),
        ),
        ...Object.fromEntries(gone.flat()),
      }));
    } catch (error) {
      console.error("Failed to refresh the rider's open rides.", error);
    }
  }, []);

  // While the My trips list is on screen, re-check the rides at the tracker's rate.
  const showingTripsList = signedIn && activeTab === "trips" && tripsView === "list";
  React.useEffect(() => {
    if (!showingTripsList) return;
    void refreshOpenTrips();
    const timer = setInterval(() => void refreshOpenTrips(), 10_000);
    return () => clearInterval(timer);
  }, [refreshOpenTrips, showingTripsList]);

  const isMobileViewport = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches;

  React.useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    // Close when tapping outside the dropdown or the logo trigger
    const onPointerDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest("[data-nav-menu],[data-nav-trigger]")) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [menuOpen]);

  const selectRide = () => {
    setActiveTab("ride");
    setMenuOpen(false);
    // Switching to Ride returns to the booking flow (and closes the sign-in
    // view if it was open)
    closeLogin();
  };

  const selectMyTrips = () => {
    setActiveTab("trips");
    setTripsView("list");
    setMenuOpen(false);
    // My trips requires an account — open the sign-in view for signed-out
    // users (the prompt behind it can reopen it too)
    if (!signedIn) openLogin("account");
  };

  // Opens one of the rider's rides on My trips. Leaving a cancelled ride drops it.
  const openTrip = (hailingRequestId: string) => {
    if (orderCancelled && selectedTripId !== hailingRequestId) {
      setActiveTrips((prev) =>
        prev.filter((trip) => trip.hailingRequestId !== selectedTripId),
      );
    }
    if (selectedTripId !== hailingRequestId) {
      setOrderCancelled(false);
      setCancelOrderError(false);
      setDriverPosition(
        activeTrips.find((trip) => trip.hailingRequestId === hailingRequestId)
          ?.driverPosition,
      );
    }
    setSelectedTripId(hailingRequestId);
    setTripsView("tracker");
  };

  // "Book a new ride" on a cancelled ride: back to a fresh booking on Ride.
  const handleBookAgain = () => {
    restart();
    setActiveTab("ride");
  };

  const openSupport = () => {
    setMenuOpen(false);
    setSupportOpen(true);
  };

  const renderStep = () => {
    // My trips tab: sign-in gated. Signed-out users see a prompt that opens
    // the sign-in view (?login=1); once signed in, show the trips screen.
    if (activeTab === "trips") {
      if (!signedIn) {
        return (
          <div className="mx-auto max-w-md rounded-2xl bg-white p-12 text-center shadow-[0_4px_16px_rgba(15,21,26,0.08)]">
            <span className="font-material-symbols text-[40px] leading-none text-[#8A8F93]">
              lock
            </span>
            <h2 className="mt-4 text-[20px] font-bold text-ink-100">
              {t("trips.signInRequired")}
            </h2>
            <p className="mt-2 text-[14px] text-[#6B7075]">
              {t("trips.signInRequiredSub")}
            </p>
            <button
              type="button"
              onClick={() => openLogin("account")}
              className="mt-6 h-[44px] rounded-[22px] border-[1.5px] border-brand-blue bg-white px-6 text-[15px] font-semibold text-brand-blue"
            >
              {t("header.signIn")}
            </button>
          </div>
        );
      }
      if (tripsView === "tracker" && activeTrip) {
        return (
          <div className="flex flex-1 flex-col">
            <button
              type="button"
              onClick={() => setTripsView("list")}
              className="-ml-1 mb-3 flex min-h-11 items-center gap-1 self-start font-semibold text-brand-blue"
            >
              <span aria-hidden="true" className="font-material-symbols text-[18px] leading-none">
                arrow_back_ios_new
              </span>
              {t("trips.backToTrips")}
            </button>
            {renderTracker()}
          </div>
        );
      }
      return (
        <TripHistory
          openTrips={openTrips.map((trip) => ({
            hailingRequestId: trip.hailingRequestId!,
            status: tripStatuses[trip.hailingRequestId!],
            pickupPlace: trip.pickupPlace,
            dropoffPlace: trip.dropoffPlace,
            scheduledTime: trip.scheduledTime,
            vehicleName: selectedVehicleClasses(trip)
              .map((vehicle) => vehicle.name)
              .join(" / "),
            fare: trip.fareEstimate,
          }))}
          onOpenTrip={openTrip}
        />
      );
    }
    switch (flowState.currentStep) {
      case "prices":
        return (
          <Prices
            state={flowState}
            setState={setFlowState}
            quote={currentQuote}
            // "Edit" on the route card returns to the "where" step so the
            // user can adjust pickup / drop-off / stops (mockup quote panel)
            onEdit={() => goToStep("where")}
            onStepComplete={() => goToStep("payment")}
          />
        );
      case "payment":
        return (
          <Payment
            state={flowState}
            isSubmitting={submittingOrder}
            orderError={orderError}
            fareChanged={fareChanged}
            onStepComplete={handleCreateOrder}
            // Back arrow on the pay panel returns to the Prices step
            // (mockup pay panel nav.quote)
            onBack={() => {
              setOrderError(false);
              setFareChanged(false);
              goToStep("prices");
            }}
          />
        );
      case "ride":
        if (!activeTrip) return renderWhereTo();
        return renderTracker();
      case "where":
      default:
        return renderWhereTo();
    }
  };

  const renderTracker = () =>
    activeTrip ? (
      <RideTracker
        // One tracker per ride: switching rides starts the next one fresh.
        key={activeTrip.hailingRequestId}
        state={activeTrip}
        initialStatus={activeTripStatus}
        isCancelling={cancellingOrder}
        cancelError={cancelOrderError}
        onCancelTrip={handleCancelOrder}
        onMapUpdate={handleRideMapUpdate}
        onStatusChange={handleTripStatus}
        isCancelled={orderCancelled}
        onBookAgain={handleBookAgain}
      />
    ) : null;

  const renderWhereTo = () => (
    <WhereTo
      state={flowState}
      setState={setFlowState}
      signedIn={signedIn}
      quote={currentQuote}
      quoteStatus={
        quoteState?.routeKey === quoteRouteKey ? quoteState.status : "idle"
      }
      submittingPrices={submittingPrices}
      // "See Prices": sign-in gate — opens the sign-in view when signed
      // out (and REQUIRE_SIGN_IN is on), straight to prices otherwise
      onStepComplete={handleSeePrices}
    />
  );

  // The map follows the screen: the active trip while its tracker is shown, the
  // booking form otherwise (no trip route or driver on the booking form).
  const showingTrip =
    activeTrip !== null &&
    (activeTab === "trips"
      ? tripsView === "tracker"
      : flowState.currentStep === "ride");
  const mapState = showingTrip && activeTrip ? activeTrip : flowState;
  // A scheduled ride shows the taxi only once the driver is heading to the pickup:
  // before that the tracker says they will share their live location nearer the
  // pickup time, so their current position is not shown. Live rides always show it.
  const driverOnTheWay =
    !mapState.scheduledTime || DRIVER_ROUTE_STATUSES.includes(activeTripStatus ?? "");

  // Show the drop-off marker/route once the user picks a destination
  // (mockup: "area" view on the where screen, route view after). The
  // destination starts empty — it's set by picking a search result.
  const showDropoff =
    showingTrip ||
    mapState.currentStep !== "where" || mapState.dropoffPlace !== "";
  const pickupPosition = React.useMemo(
    () =>
      mapState.pickupLat != null && mapState.pickupLng != null
        ? { lat: mapState.pickupLat, lng: mapState.pickupLng }
        : undefined,
    [mapState.pickupLat, mapState.pickupLng],
  );
  const dropoffPosition = React.useMemo(
    () =>
      showDropoff && mapState.dropoffLat != null && mapState.dropoffLng != null
        ? { lat: mapState.dropoffLat, lng: mapState.dropoffLng }
        : undefined,
    [showDropoff, mapState.dropoffLat, mapState.dropoffLng],
  );
  const stopPosition = React.useMemo(
    () =>
      mapState.stopLat != null && mapState.stopLng != null
        ? { lat: mapState.stopLat, lng: mapState.stopLng }
        : undefined,
    [mapState.stopLat, mapState.stopLng],
  );
  const handleRideMapUpdate = React.useCallback(
    (
      routePolyline: string | null | undefined,
      position: { lat: number; lng: number } | null | undefined,
      operatingArea: string | null | undefined,
    ) => {
      setActiveTrips((trips) => {
        const index = trips.findIndex(
          (trip) => trip.hailingRequestId === selectedTripId,
        );
        const prev = trips[index];
        if (
          !prev ||
          ((!routePolyline || prev.routePolyline === routePolyline) &&
            (!operatingArea || prev.driverOperatingArea === operatingArea))
        ) {
          return trips;
        }
        const next = [...trips];
        next[index] = {
          ...prev,
          ...(routePolyline ? { routePolyline } : {}),
          ...(operatingArea ? { driverOperatingArea: operatingArea } : {}),
        };
        return next;
      });
      setDriverPosition((previous) => {
        if (
          previous?.lat === position?.lat &&
          previous?.lng === position?.lng
        ) {
          return previous;
        }
        return position ?? undefined;
      });
    },
    [selectedTripId],
  );

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-[#EEF1F4]">
      <HailingHeader
        activeTab={activeTab}
        menuOpen={menuOpen}
        onMenuTrigger={() => {
          // Menu trigger is mobile-only; on desktop the tabs stay visible
          if (isMobileViewport()) setMenuOpen((o) => !o);
        }}
        onSelectRide={selectRide}
        onSelectMyTrips={selectMyTrips}
        onOpenSupport={openSupport}
        signedIn={signedIn}
        onSignIn={() => openLogin("account")}
      />

      {/* Content: mockup Layout A — booking panel left, map right.
          Mobile (no mockup design): map strip on top, panel below. */}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* Map — fills right side on desktop,
            fixed strip above the panel on mobile */}
        <div className="relative h-44 shrink-0 md:order-2 md:h-auto md:flex-1">
          <MapView
            start={pickupPosition}
            end={dropoffPosition}
            stop={stopPosition}
            encodedPolyline={mapState.routePolyline}
            driverPosition={showingTrip && driverOnTheWay ? driverPosition : undefined}
            driverOperatingArea={mapState.driverOperatingArea}
            zoom={showDropoff ? 12 : 15}
          />
        </div>

        {/* Booking panel — wider on larger screens to keep vehicle names readable. */}
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-[#F2F3F3] p-5 md:order-1 md:w-[480px] md:flex-none md:p-7 md:shadow-[1px_0_0_#E3E4E5] lg:w-[500px]">
          {renderStep()}
        </main>
      </div>

      {loginOpen && <SignInDialog onSuccess={handleLoginSuccess} />}

      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
};

export default HailingFlow;
